import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  LinearProgress,
  Autocomplete,
  Chip
} from '@mui/material';
import {
  Save as SaveIcon
} from '@mui/icons-material';
import { inventoryService } from '../services/InventoryService';
import { Item } from '../models/Item';
import { getCategoriesForShop, getDefaultCategoryForShop } from '../utils/categories';

const BulkEditDialog = ({ open, onClose, shopName, items, onUpdate }) => {
  const [editedItems, setEditedItems] = useState([]);
  const [applying, setApplying] = useState(false);
  const [errors, setErrors] = useState([]);
  const [editingCell, setEditingCell] = useState(null);

  useEffect(() => {
    if (open && items.length > 0) {
      // Initialize edited items with current item data
      setEditedItems(items.map(item => ({
        id: item.id,
        name: item.name || '',
        brand: item.brand || '',
        category: item.category || getDefaultCategoryForShop(shopName),
        description: item.description || '',
        quantity: item.quantity || 0,
        sellingPrice: item.sellingPrice || 0,
        originalCost: item.originalCost || 0
      })));
      setErrors([]);
      setEditingCell(null);
    }
  }, [open, items, shopName]);

  const handleCellChange = (itemIndex, field, value) => {
    const updated = [...editedItems];
    const item = updated[itemIndex];

    if (field === 'quantity') {
      item[field] = parseInt(value) || 0;
    } else if (field === 'sellingPrice' || field === 'originalCost') {
      item[field] = parseFloat(value) || 0;
    } else {
      item[field] = value;
    }

    setEditedItems(updated);
  };

  const handleKeyDown = (e, itemIndex, field) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      // Move to next cell or next row
      const nextField = getNextField(field);
      if (nextField) {
        const nextIndex = nextField === 'name' ? itemIndex + 1 : itemIndex;
        if (nextIndex < editedItems.length) {
          setEditingCell({ rowIndex: nextIndex, field: nextField });
        }
      }
    } else if (e.key === 'Tab') {
      e.preventDefault();
      // Move to next cell
      const nextField = getNextField(field);
      if (nextField) {
        setEditingCell({ rowIndex: itemIndex, field: nextField });
      } else if (itemIndex < editedItems.length - 1) {
        setEditingCell({ rowIndex: itemIndex + 1, field: 'name' });
      }
    } else if (e.key === 'Escape') {
      setEditingCell(null);
    }
  };

  const getNextField = (currentField) => {
    const fields = ['name', 'brand', 'category', 'description', 'quantity', 'sellingPrice', 'originalCost'];
    const currentIndex = fields.indexOf(currentField);
    return currentIndex < fields.length - 1 ? fields[currentIndex + 1] : null;
  };

  const validateItems = () => {
    const validationErrors = [];

    editedItems.forEach((item, index) => {
      if (!item.name || item.name.trim() === '') {
        validationErrors.push(`Row ${index + 1}: Name is required`);
      }
      if (item.quantity < 0) {
        validationErrors.push(`Row ${index + 1}: Quantity cannot be negative`);
      }
      if (item.sellingPrice < 0) {
        validationErrors.push(`Row ${index + 1}: Selling price cannot be negative`);
      }
      if (item.originalCost < 0) {
        validationErrors.push(`Row ${index + 1}: Original cost cannot be negative`);
      }
      if (!item.category || item.category.trim() === '') {
        validationErrors.push(`Row ${index + 1}: Category is required`);
      }
    });

    setErrors(validationErrors);
    return validationErrors.length === 0;
  };

  const handleSave = async () => {
    if (editedItems.length === 0) {
      setErrors(['No items to save']);
      return;
    }

    if (!validateItems()) {
      return;
    }

    setApplying(true);
    setErrors([]);

    try {
      const failedItems = [];
      const successfulItems = [];

      for (let i = 0; i < editedItems.length; i++) {
        const editedItem = editedItems[i];
        const originalItem = items.find(item => item.id === editedItem.id);

        if (!originalItem) {
          failedItems.push({ name: editedItem.name, error: 'Original item not found' });
          continue;
        }

        try {
          // Check if item has changed
          const hasChanges =
            editedItem.name !== originalItem.name ||
            editedItem.brand !== originalItem.brand ||
            editedItem.category !== originalItem.category ||
            editedItem.description !== originalItem.description ||
            editedItem.quantity !== originalItem.quantity ||
            editedItem.sellingPrice !== originalItem.sellingPrice ||
            editedItem.originalCost !== originalItem.originalCost;

          if (hasChanges) {
            const updatedItem = new Item({
              ...originalItem,
              name: editedItem.name,
              brand: editedItem.brand,
              category: editedItem.category,
              description: editedItem.description,
              quantity: editedItem.quantity,
              sellingPrice: editedItem.sellingPrice,
              originalCost: editedItem.originalCost
            });

            await inventoryService.updateItem(updatedItem);
            successfulItems.push(updatedItem);
          }
        } catch (error) {
          console.error(`Error updating ${editedItem.name}:`, error);
          failedItems.push({ name: editedItem.name, error: error.message });
        }
      }

      if (failedItems.length > 0) {
        const failedNames = failedItems.map(f => f.name).join(', ');
        setErrors([`Failed to update some items: ${failedNames}`]);
      } else {
        // Success - reload items
        if (onUpdate) {
          await onUpdate();
        }
        handleClose();
      }
    } catch (error) {
      setErrors([`Error saving changes: ${error.message}`]);
      console.error('Error saving bulk changes:', error);
    } finally {
      setApplying(false);
    }
  };

  const handleClose = () => {
    setEditedItems([]);
    setErrors([]);
    setEditingCell(null);
    onClose();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-PH', {
      style: 'currency',
      currency: 'PHP'
    }).format(amount);
  };

  const renderEditableCell = (item, itemIndex, field, value, isNumeric = false, isInteger = false) => {
    const isEditing = editingCell?.rowIndex === itemIndex && editingCell?.field === field;

    if (isEditing) {
      if (field === 'category') {
        return (
          <Autocomplete
            freeSolo
            size="small"
            options={getCategoriesForShop(shopName)}
            value={value}
            onChange={(e, newValue) => {
              handleCellChange(itemIndex, field, newValue || '');
              setEditingCell(null);
            }}
            onInputChange={(e, newInputValue) => {
              handleCellChange(itemIndex, field, newInputValue || '');
            }}
            onBlur={() => setEditingCell(null)}
            renderInput={(params) => (
              <TextField {...params} size="small" placeholder="Category" autoFocus />
            )}
            disableClearable
            fullWidth
          />
        );
      }

      return (
        <TextField
          size="small"
          value={value}
          onChange={(e) => handleCellChange(itemIndex, field, e.target.value)}
          onKeyDown={(e) => handleKeyDown(e, itemIndex, field)}
          onBlur={() => setEditingCell(null)}
          type={isNumeric ? 'number' : 'text'}
          inputProps={{
            min: 0,
            step: isInteger ? 1 : 0.01,
            style: { textAlign: isNumeric ? 'right' : 'left' }
          }}
          autoFocus
          fullWidth
        />
      );
    }

    return (
      <Box
        onClick={() => setEditingCell({ rowIndex: itemIndex, field })}
        sx={{
          cursor: 'pointer',
          '&:hover': { backgroundColor: 'rgba(229,57,53,0.06)' },
          padding: '4px 8px',
          borderRadius: '4px',
          minHeight: '32px',
          display: 'flex',
          alignItems: 'center',
          textAlign: isNumeric ? 'right' : 'left',
          justifyContent: isNumeric ? 'flex-end' : 'flex-start'
        }}
      >
        {isNumeric && (field === 'sellingPrice' || field === 'originalCost')
          ? formatCurrency(value || 0)
          : value || ''
        }
      </Box>
    );
  };

  if (editedItems.length === 0) {
    return (
      <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
        <DialogTitle>Bulk Edit Items</DialogTitle>
        <DialogContent>
          <Typography>No items to edit.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xl" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Bulk Edit Items (Excel-like)</Typography>
          <Chip
            label={`${editedItems.length} items`}
            color="primary"
            size="small"
          />
        </Box>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Click any cell to edit. Use Enter to move to next cell, Tab to move horizontally, Escape to cancel editing.
        </Typography>

        {errors.length > 0 && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errors.map((error, idx) => (
              <Typography key={idx} variant="body2">• {error}</Typography>
            ))}
          </Alert>
        )}

        <TableContainer component={Paper} sx={{ maxHeight: '60vh' }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#3A3A3A', width: 150 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#3A3A3A', width: 120 }}>Brand</TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#3A3A3A', width: 100 }}>Category</TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#3A3A3A', width: 200 }}>Description</TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#3A3A3A', width: 100 }}>Quantity</TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#3A3A3A', width: 120 }}>Selling Price</TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#3A3A3A', width: 120 }}>Original Cost</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {editedItems.map((item, index) => (
                <TableRow key={item.id} hover>
                  <TableCell>
                    {renderEditableCell(item, index, 'name', item.name)}
                  </TableCell>
                  <TableCell>
                    {renderEditableCell(item, index, 'brand', item.brand)}
                  </TableCell>
                  <TableCell>
                    {renderEditableCell(item, index, 'category', item.category)}
                  </TableCell>
                  <TableCell>
                    {renderEditableCell(item, index, 'description', item.description)}
                  </TableCell>
                  <TableCell>
                    {renderEditableCell(item, index, 'quantity', item.quantity, true, true)}
                  </TableCell>
                  <TableCell>
                    {renderEditableCell(item, index, 'sellingPrice', item.sellingPrice, true)}
                  </TableCell>
                  <TableCell>
                    {renderEditableCell(item, index, 'originalCost', item.originalCost, true)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {applying && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
              Saving changes to {editedItems.length} item(s)...
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={applying}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          startIcon={<SaveIcon />}
          disabled={applying}
        >
          Save All Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BulkEditDialog;
